import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../config/api';
import './DisclosureRecipientPage.css';

const DisclosureRecipientPage = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  // Pre-metadata hint: the URL prefix (/disclosure/ vs /shared/) already tells
  // us whether this is a folder share, so the loading / early-error branches
  // can render the right wording before the metadata fetch resolves.
  const pathIsFolderShare = typeof window !== 'undefined'
      && window.location.pathname.startsWith('/shared/');

  const [metadata, setMetadata] = useState(null);
  const [metadataError, setMetadataError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [step, setStep] = useState('form'); // 'form' | 'otp' | 'success'
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [otpSentAt, setOtpSentAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.publicDisclosureMetadata(shareToken));
        setMetadata(res.data);
      } catch (e) {
        setMetadataError(e?.response?.data?.message || 'This link is no longer active.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [shareToken]);

  const handleSubmitInfo = async (evt) => {
    evt.preventDefault();
    setFormError(null);
    if (!email || !firstName || !lastName || !phone) {
      setFormError('Please fill in every field.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.publicDisclosureRecipientInfo(shareToken),
        { email: email.trim().toLowerCase(), firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() }
      );
      setOtpSentAt(Date.now());
      setStep('otp');
    } catch (e) {
      setFormError(e?.response?.data?.message || 'Could not send code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (evt) => {
    evt.preventDefault();
    setOtpError(null);
    if (!/^\d{6}$/.test(otp)) {
      setOtpError('Enter the 6-digit code from the email.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.publicDisclosureVerifyOtp(shareToken),
        { email: email.trim().toLowerCase(), code: otp }
      );
      // Product decision: don't auto-open the viewer here. The verify-otp
      // backend also emails a "View Disclosure Documents" link to the recipient
      // — the client only confirms that landed and asks them to open it from
      // the inbox. Keeps a single audit trail on the emailed link and prevents
      // this browser tab from becoming the effective share URL.
      setStep('success');
    } catch (e) {
      setOtpError(e?.response?.data?.message || 'That code did not match. Please check your email again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setOtpError(null);
    setIsSubmitting(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.publicDisclosureRecipientInfo(shareToken),
        { email: email.trim().toLowerCase(), firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() }
      );
      setOtpSentAt(Date.now());
    } catch (e) {
      setOtpError(e?.response?.data?.message || 'Could not resend code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="disclosure-public-shell">
        <div className="disclosure-public-card">
          <div className="disclosure-public-header">
            <h1>{pathIsFolderShare ? 'Rialty Documents' : 'Rialty Disclosures'}</h1>
            <p>{pathIsFolderShare ? 'Preparing your secure link' : 'Preparing your secure disclosure link'}</p>
          </div>
          <div className="disclosure-public-body disclosure-public-loading">
            <div className="disclosure-public-spinner" aria-hidden="true" />
            <p className="disclosure-public-loading-text">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (metadataError || !metadata) {
    return (
      <div className="disclosure-public-shell">
        <div className="disclosure-public-card">
          <div className="disclosure-public-header">
            <h1>{pathIsFolderShare ? 'Rialty Documents' : 'Rialty Disclosures'}</h1>
          </div>
          <div className="disclosure-public-body">
            <p className="disclosure-public-error">{metadataError || 'This link is no longer active.'}</p>
            <p className="disclosure-public-hint">If you received this link recently, ask the sender to share a fresh one.</p>
          </div>
        </div>
      </div>
    );
  }

  // Metadata now carries `folder` — anything other than "disclosure" is a
  // Transaction Documents folder share and should read as generic "Documents"
  // instead of "Disclosures". Same recipient flow either way; wording only.
  // Disclosure flow copy is kept byte-for-byte identical to the pre-folder
  // implementation to avoid any regressions on the shipped flow.
  const isFolderShare = !!metadata.folder && metadata.folder !== 'disclosure';
  const titleLabel = isFolderShare ? 'Rialty Documents' : 'Rialty Disclosures';
  const introLine = isFolderShare
    ? (metadata.agentName ? `${metadata.agentName} shared documents for` : 'Shared documents for')
    : (metadata.agentName ? `${metadata.agentName} shared disclosure documents for` : 'Disclosure documents shared for');

  if (metadata.isRevoked) {
    return (
      <div className="disclosure-public-shell">
        <div className="disclosure-public-card">
          <div className="disclosure-public-header">
            <h1>{titleLabel}</h1>
          </div>
          <div className="disclosure-public-body">
            <p className="disclosure-public-error">This link has been revoked by the sender.</p>
            <p className="disclosure-public-hint">Please contact {metadata.agentName || 'the sending agent'} to request access again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="disclosure-public-shell">
      <div className="disclosure-public-card">
        <div className="disclosure-public-header">
          <h1>{titleLabel}</h1>
          <p>
            {introLine}
            <br />
            <strong>{metadata.propertyAddress}</strong>
          </p>
        </div>

        {step === 'form' && (
          <form className="disclosure-public-body" onSubmit={handleSubmitInfo} noValidate>
            <p className="disclosure-public-lead">
              Enter your details to receive a one-time verification code by email.
            </p>
            <div className="disclosure-public-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="disclosure-public-field-row">
              <div className="disclosure-public-field">
                <label htmlFor="firstName">First name</label>
                <input id="firstName" type="text" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </div>
              <div className="disclosure-public-field">
                <label htmlFor="lastName">Last name</label>
                <input id="lastName" type="text" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
            </div>
            <div className="disclosure-public-field">
              <label htmlFor="phone">Phone</label>
              <input id="phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            </div>
            {formError && <p className="disclosure-public-error">{formError}</p>}
            <button className="disclosure-public-primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending code…' : 'Get the Link'}
            </button>
            <p className="disclosure-public-fineprint">
              We'll email a 6-digit code to verify it's you before showing the documents.
            </p>
          </form>
        )}

        {step === 'otp' && (
          <form className="disclosure-public-body" onSubmit={handleVerifyOtp} noValidate>
            <p className="disclosure-public-lead">
              We emailed a 6-digit code to <strong>{email}</strong>. Enter it below to continue.
            </p>
            <div className="disclosure-public-field">
              <label htmlFor="otp">Verification code</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="disclosure-public-otp-input"
                required
              />
            </div>
            {otpError && <p className="disclosure-public-error">{otpError}</p>}
            <button className="disclosure-public-primary" type="submit" disabled={isSubmitting || otp.length !== 6}>
              {isSubmitting ? 'Verifying…' : 'Verify'}
            </button>
            <div className="disclosure-public-resend-row">
              <button type="button" className="disclosure-public-link-btn" onClick={handleResend} disabled={isSubmitting}>
                Resend code
              </button>
              {otpSentAt && (
                <span className="disclosure-public-fineprint">
                  Last sent {new Date(otpSentAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
            <button
              type="button"
              className="disclosure-public-link-btn disclosure-public-back"
              onClick={() => { setStep('form'); setOtp(''); setOtpError(null); }}
              disabled={isSubmitting}
            >
              Change email
            </button>
          </form>
        )}

        {step === 'success' && (
          <div className="disclosure-public-body disclosure-public-success">
            <div className="disclosure-public-success-icon" aria-hidden="true">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h2 className="disclosure-public-success-title">Check your email</h2>
            <p className="disclosure-public-success-body">
              You're verified. We just sent a private link to{' '}
              <strong>{email}</strong> — open it from your inbox to view the
              {isFolderShare ? ' shared documents.' : ' disclosure documents.'}
            </p>
            <p className="disclosure-public-fineprint">
              The link doesn't expire; you can bookmark it or forward it if the
              sender allows.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisclosureRecipientPage;
